import { Link } from 'react-router-dom';
import appHomeScreen from '../../assets/dala-home-screen-clean.webp';
import { site } from '../../site';
import { PageTitle } from '../PageTitle';
import { FacebookIcon } from './FacebookIcon';
import { GetAppButton } from './GetAppButton';

const SERVICES = [
  {
    id: 'sakay',
    name: 'Sakay',
    gloss: 'Rides',
    subtitle: 'Trike & motor · from ₱30',
    body: 'A trike or motorcycle to the next barangay, or across town. Cash when you arrive.',
  },
  {
    id: 'pagkaon',
    name: 'Pagkaon',
    gloss: 'Food',
    subtitle: 'From the karinderya down the road',
    body: 'Order from kitchens you already know. Cash on delivery — no card, no wallet top-up.',
  },
  {
    id: 'tindahan',
    name: 'Tindahan',
    gloss: 'Stores',
    subtitle: 'Stores · pharmacies · groceries',
    body: 'Order from registered local shops with live catalogs, prices, and availability.',
  },
  {
    id: 'papalit',
    name: 'Papalit',
    gloss: 'Shop for me',
    subtitle: 'Any store · your own list',
    body: 'Choose a store that is not on Dala, send a list, and approve the shopper’s photos and exact total before they pay.',
  },
  {
    id: 'padala',
    name: 'Padala',
    gloss: 'Parcels',
    subtitle: 'Send a parcel across town',
    body: 'Documents, pasalubong, a bag of rice. Tracked from pickup to the door.',
  },
] as const;

const STEPS = [
  {
    n: '01',
    title: 'Tell us where',
    body: 'Pin a place, search an address, or send a rider to a store with a list.',
  },
  {
    n: '02',
    title: 'A neighbour picks it up',
    body: 'Drivers, shoppers and stores on Dala live in Magsaysay, Bansalan and Matanao. You see who is coming.',
  },
  {
    n: '03',
    title: 'Pay in cash',
    body: 'Fares and orders settle when they arrive. No card, no app wallet for the pilot.',
  },
] as const;

export function HomePage() {
  return (
    <>
      <PageTitle>dala — rides, food, stores, parcels</PageTitle>

      <section className="bg-ink text-white">
        <div className="mx-auto grid max-w-6xl items-center gap-12 px-5 pb-20 pt-10 md:grid-cols-2 md:px-8 md:pb-28 md:pt-16">
          <div>
            <p className="overline !text-white/40">{site.region}</p>
            <h1 className="mt-4 max-w-lg text-[40px] leading-[1.05] tracking-display md:text-[56px]">
              All of MABAMA,
              <br />
              in your pocket.
            </h1>
            <p className="mt-5 max-w-md text-[15px] font-medium leading-relaxed text-white/55">
              Rides, food, stores and parcels — one app, run by people from
              here.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <GetAppButton />
              <a
                href={site.facebookUrl}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center justify-center gap-2 rounded-button border border-white/15 px-5 py-3 font-display font-extrabold text-white transition hover:bg-white/10"
              >
                <FacebookIcon />
                Facebook
              </a>
              <Link
                to="/support"
                className="inline-flex items-center px-2 text-sm font-bold text-terracotta hover:underline"
              >
                Help & support
              </Link>
            </div>
            <div className="mt-8 flex flex-wrap gap-[7px]">
              {SERVICES.map((s, i) => (
                <span
                  key={s.id}
                  className={
                    'service-pill ' +
                    (i === 0 ? 'bg-mango text-ink' : 'bg-white/12 text-white')
                  }
                >
                  {s.name}
                </span>
              ))}
            </div>
          </div>
          <AppPreview />
        </div>
      </section>

      <section id="services" className="bg-paper text-ink">
        <div className="mx-auto max-w-6xl px-5 py-20 md:px-8">
          <p className="overline">What Dala does</p>
          <h2 className="mt-3 max-w-xl text-3xl leading-tight md:text-4xl">
            Five ways home gets closer.
          </h2>
          <div className="mt-10 grid gap-4 md:grid-cols-2">
            {SERVICES.map((s) => (
              <article key={s.id} className="card p-6">
                <div className="flex items-baseline justify-between gap-3">
                  <h3 className="text-[19px]">{s.name}</h3>
                  <span className="text-xs font-bold text-ink/40">{s.gloss}</span>
                </div>
                <p className="mt-1 text-[13px] font-semibold text-ink/50">
                  {s.subtitle}
                </p>
                <p className="mt-3 text-[13px] font-medium leading-relaxed text-ink/55">
                  {s.body}
                </p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-clay text-ink">
        <div className="mx-auto max-w-6xl px-5 py-20 md:px-8">
          <p className="overline">How it works</p>
          <h2 className="mt-3 text-3xl md:text-4xl">Three steps. Cash at the door.</h2>
          <div className="mt-10 grid gap-4 md:grid-cols-3">
            {STEPS.map((step) => (
              <article key={step.n} className="rounded-panel bg-paper p-6">
                <div className="font-display text-sm font-extrabold text-mango">
                  {step.n}
                </div>
                <h3 className="mt-3 text-[19px]">{step.title}</h3>
                <p className="mt-2 text-[13px] font-medium leading-relaxed text-ink/55">
                  {step.body}
                </p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-paper text-ink">
        <div className="mx-auto grid max-w-6xl gap-4 px-5 py-20 md:grid-cols-2 md:px-8">
          <article className="rounded-frame bg-ink p-8 text-white md:p-10">
            <p className="overline !text-white/35">Drive with Dala</p>
            <h2 className="mt-3 text-[28px] leading-tight">
              Your trike, your hours.
            </h2>
            <p className="mt-3 text-[13px] font-medium leading-relaxed text-white/55">
              Apply in the app as a rider. We review papers, then you go online
              for sakay and padala around Magsaysay, Bansalan and Matanao.
            </p>
          </article>
          <article className="rounded-frame bg-mango p-8 text-ink md:p-10">
            <p className="overline">Sell with Dala</p>
            <h2 className="mt-3 text-[28px] leading-tight">
              Put the karinderya on the map.
            </h2>
            <p className="mt-3 text-[13px] font-medium leading-relaxed text-ink/70">
              Merchants list food and goods. Customers pay cash on delivery.
              Apply from the account tab in the app.
            </p>
          </article>
        </div>
      </section>
    </>
  );
}

function AppPreview() {
  return (
    <figure className="mx-auto w-full max-w-[320px]">
      <div className="overflow-hidden rounded-[38px] border border-white/10 bg-white/[0.06] p-2.5 shadow-[0_30px_80px_-20px_rgba(0,0,0,0.45)]">
        <img
          src={appHomeScreen}
          alt="Dala app home screen showing rides, food, shops, shopping help, and parcel delivery"
          width={850}
          height={1850}
          loading="eager"
          fetchPriority="high"
          decoding="async"
          className="block h-auto w-full rounded-[29px]"
        />
      </div>
      <figcaption className="mt-4 text-center text-xs font-semibold text-white/45">
        One app for rides, food, shops, and parcels.
      </figcaption>
    </figure>
  );
}
